import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { TinyHouseModel } from '@/types'

import Link from 'next/link'
import { ArrowRight, Ruler, Users, Weight } from 'lucide-react'
import { CmsImage } from '@/components/generals/cms-image'

interface ModelCardProps {
    model: TinyHouseModel
    headingLevel?: 'h2' | 'h3'
}

type SpecField = 'length' | 'sleeps' | 'weight'

const CARD_SPECS: { field: SpecField, label: string, icon: LucideIcon }[] = [
    { field: 'length', label: "Length", icon: Ruler },
    { field: 'sleeps', label: "Sleeps", icon: Users },
    { field: 'weight', label: "Weight", icon: Weight }
]

export const ModelCard: FC<ModelCardProps> = ({ model, headingLevel: Heading = 'h3' }): ReactNode => {
    const href = `/models/${model.slug}`
    const specs = CARD_SPECS.filter(spec => model[spec.field])

    return (
        <article className='group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_2px_rgb(0_0_0/0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_-28px_rgb(31_43_36/0.45)] motion-reduce:transition-none motion-reduce:hover:translate-y-0'>
            <div className='relative aspect-[4/3] overflow-hidden bg-secondary'>
                <Link href={href} tabIndex={-1} aria-hidden='true' className='block size-full'>
                    <CmsImage
                        src={model.cover_image}
                        alt={model.cover_image_alt || model.name}
                        className='transition-transform duration-700 group-hover:scale-[1.04] motion-reduce:transition-none'
                    />
                </Link>

                {model.best_for && (
                    <span className='pointer-events-none absolute top-4 left-4 max-w-[calc(100%-2rem)] rounded-full bg-background/90 px-3 py-1.5 text-xs font-semibold text-foreground shadow-sm backdrop-blur'>
                        <span className='pointer-events-auto' data-mdlfy-cms={`models:${model.id}:best_for`}>{model.best_for}</span>
                    </span>
                )}
            </div>

            <div className='flex flex-1 flex-col p-6'>
                <div className='flex items-start justify-between gap-4'>
                    <Heading className='font-heading text-[1.85rem] leading-none font-medium tracking-tight'>
                        <Link
                            href={href}
                            className='rounded-md outline-none hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/40'
                        >
                            <span data-mdlfy-cms={`models:${model.id}:name`}>{model.name}</span>
                        </Link>
                    </Heading>

                    {model.price && (
                        <p className='shrink-0 text-right'>
                            <span className='block text-[0.68rem] font-semibold tracking-[0.16em] text-muted-foreground uppercase'>
                                From
                            </span>

                            <span className='font-heading text-xl font-medium' data-mdlfy-cms={`models:${model.id}:price`}>{model.price}</span>
                        </p>
                    )}
                </div>

                {model.tagline && (
                    <p className='mt-3 leading-relaxed text-muted-foreground' data-mdlfy-cms={`models:${model.id}:tagline`}>{model.tagline}</p>
                )}

                {specs.length > 0 && (
                    <dl className='mt-6 grid grid-cols-3 gap-3 border-t border-border pt-5'>
                        {specs.map(spec => (
                            <div key={spec.field}>
                                <dt className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                                    <spec.icon className='size-4' aria-hidden='true' />
                                    {spec.label}
                                </dt>

                                <dd className='mt-1.5 text-sm font-semibold'>
                                    <span data-mdlfy-cms={`models:${model.id}:${spec.field}`}>{model[spec.field]}</span>
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}

                <div className='mt-auto pt-6'>
                    <Link
                        href={href}
                        tabIndex={-1}
                        aria-hidden='true'
                        className='inline-flex items-center gap-1.5 text-sm font-semibold text-primary'
                    >
                        See details
                        <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                    </Link>
                </div>
            </div>
        </article>
    )
}
